/**
 * Client for crm-email's operations (segments_*, email_*, broadcasts_*; CONTRACTS §7, §7a).
 * Every call uses the real operation; a failure throws ApiError for the screen to show.
 */
import { api, ApiError, qs } from "./api";
import type { Broadcast, BroadcastPreview, EmailDoc, EmailPreview, EmailTopic, Segment, SegmentCounts, SegmentDefinition, SegmentMember } from "./types";

// ---- segments ----

export async function listSegments(p: { brand_id?: number; q?: string; include_archived?: boolean } = {}): Promise<Segment[]> {
  const r = await api<{ segments: Segment[] }>("/api/v1/segments" + qs({ ...p, limit: 500 }));
  return r.segments ?? [];
}

export function getSegment(id: number): Promise<Segment> {
  return api<Segment>(`/api/v1/segments/${id}`);
}

export function saveSegment(v: { id?: number; name: string; brand_id?: number; description?: string; definition: SegmentDefinition }): Promise<Segment> {
  const body = { name: v.name, brand_id: v.brand_id ?? 0, description: v.description ?? "", definition: v.definition };
  return v.id ? api<Segment>(`/api/v1/segments/${v.id}`, { method: "PATCH", json: body }) : api<Segment>("/api/v1/segments", { json: body });
}

export async function archiveSegment(id: number): Promise<void> {
  await api(`/api/v1/segments/${id}/archive`, { method: "POST" });
}

export function countSegment(v: { segment_id?: number; definition?: SegmentDefinition; brand_id?: number }): Promise<SegmentCounts> {
  return api<SegmentCounts>("/api/v1/segments/count", { json: v });
}

export async function segmentMembers(v: { segment_id?: number; definition?: SegmentDefinition; brand_id?: number; limit?: number }): Promise<SegmentMember[]> {
  const r = await api<{ members: SegmentMember[] }>("/api/v1/segments/members", { json: { ...v, limit: v.limit ?? 10 } });
  return r.members ?? [];
}

// ---- email documents and previews ----

export interface EmailDraft {
  brand_id: number;
  name: string;
  subject: string;
  preheader?: string;
  from_name?: string;
  from_email?: string;
  reply_to?: string;
  html: string;
  footer?: "auto" | "none";
}

export function getEmail(id: number): Promise<EmailDoc> {
  return api<EmailDoc>(`/api/v1/emails/${id}?include_html=true`);
}

/** email_topics_list for one brand. */
export async function listTopics(brandId: number): Promise<EmailTopic[]> {
  const r = await api<{ topics: EmailTopic[] }>("/api/v1/email/topics" + qs({ brand_id: brandId }));
  return r.topics ?? [];
}

/** email_label_set: change an email's kind and/or topic (reason required; confirm to make it transactional). */
export async function setEmailLabel(emailId: number, v: { kind?: "marketing" | "transactional"; topic_id?: number; reason: string; confirm?: boolean }): Promise<EmailDoc> {
  return api<EmailDoc>(`/api/v1/emails/${emailId}/label`, { json: { ...v, confirm: v.confirm || undefined } });
}

/** email_topics_assign: one email's topic (0 = none). */
export async function assignTopic(emailId: number, topicId: number): Promise<void> {
  await api("/api/v1/email/topics/assign", { json: { email_ids: [emailId], topic_id: topicId } });
}

export function saveEmail(id: number | null, d: EmailDraft): Promise<EmailDoc> {
  return id
    ? api<EmailDoc>(`/api/v1/emails/${id}`, {
        method: "PATCH",
        json: { name: d.name, subject: d.subject, preheader: d.preheader, from_name: d.from_name || undefined, from_email: d.from_email || undefined, reply_to: d.reply_to || undefined, html: d.html, footer: d.footer },
      })
    : api<EmailDoc>("/api/v1/emails", { json: { ...d, kind: "broadcast", generator: "admin" } });
}

/** Exact previews (light + dark, phone + desktop) as images from the server's renderer. */
export function renderPreview(v: { email_id?: number; html?: string; brand_id?: number; subject?: string; preheader?: string; contact_id?: number; dark_client?: string; kind?: string }): Promise<EmailPreview> {
  return api<EmailPreview>("/api/v1/emails/preview", { json: { ...v, layout: "separate", widths: ["phone", "desktop"] } });
}

export interface TestRecipients {
  test_list: string[];
  saved_extras: string[];
  max_extras: number;
}

export interface TestSendPreview {
  what?: string;
  email?: string;
  subject?: string;
  test_list?: string[];
  extra_people?: string[];
  note?: string;
}

export type TestSendResult =
  | { kind: "sent"; results: { to?: string; queued: boolean; skipped?: string }[] }
  | { kind: "confirm"; preview: TestSendPreview; message: string };

/** email_test_recipients_list: Brandon's test list and his saved extra people. */
export function getTestRecipients(): Promise<TestRecipients> {
  return api<TestRecipients>("/api/v1/email/test-recipients");
}

/** email_send_test. With extra people, the first call (confirm=false) answers confirm_required with a preview. */
export async function sendTest(emailId: number, p: { to: string[]; extra?: string[]; confirm?: boolean; subject?: string }): Promise<TestSendResult> {
  try {
    const r = await api<{ results: { to?: string; queued: boolean; skipped?: string }[] }>(`/api/v1/emails/${emailId}/test`, {
      json: { id: emailId, to: p.to.length ? p.to : undefined, extra: p.extra?.length ? p.extra : undefined, confirm: p.confirm || undefined, subject: p.subject || undefined },
    });
    return { kind: "sent", results: r.results ?? [] };
  } catch (e) {
    if (e instanceof ApiError && e.code === "confirm_required") return { kind: "confirm", preview: (e.details ?? {}) as TestSendPreview, message: e.message };
    throw e;
  }
}

// ---- broadcasts ----

export async function listBroadcasts(p: { brand_id?: number; status?: string } = {}): Promise<Broadcast[]> {
  const r = await api<{ broadcasts: Broadcast[] }>("/api/v1/broadcasts" + qs({ ...p, limit: 200 }));
  return r.broadcasts ?? [];
}

export function getBroadcast(id: number): Promise<Broadcast> {
  return api<Broadcast>(`/api/v1/broadcasts/${id}`);
}

export function saveBroadcast(
  id: number | null,
  v: { name: string; email_id: number; segment_id?: number; definition?: SegmentDefinition; brand_id: number; subject_b?: string },
): Promise<Broadcast> {
  return id
    ? api<Broadcast>(`/api/v1/broadcasts/${id}`, {
        method: "PATCH",
        json: { name: v.name, email_id: v.email_id, segment_id: v.segment_id, definition: v.definition, subject_b: v.subject_b ?? "" },
      })
    : api<Broadcast>("/api/v1/broadcasts", { json: { ...v, subject_b: v.subject_b || undefined } });
}

/** Step 1 (confirm=false): the server answers confirm_required with what would happen.
 *  Step 2 (confirm=true, after the person says yes): schedules it. */
export async function scheduleBroadcast(id: number, sendAt: string, confirm: boolean): Promise<{ preview?: BroadcastPreview; broadcast?: Broadcast }> {
  try {
    const b = await api<Broadcast>(`/api/v1/broadcasts/${id}/schedule`, { json: { send_at: sendAt, confirm } });
    return { broadcast: b };
  } catch (e) {
    if (e instanceof ApiError && e.code === "confirm_required") return { preview: e.details as BroadcastPreview };
    throw e;
  }
}

export function broadcastAction(id: number, action: "pause" | "resume" | "cancel"): Promise<Broadcast> {
  return api<Broadcast>(`/api/v1/broadcasts/${id}/${action}`, { method: "POST" });
}
